import React, { useEffect, useState, useRef } from "react";
import { usePostApiDevicesIdSync, useGetApiDevicesIdFlightCheck } from "@charphq/api-client";
import { EntityCard } from "./EntityCard";
import { EntityTypeBadge } from "./entityTypeMeta";
import { Badge } from "../../base/badge";
import { cn } from "@charphq/ui/lib/utils";
import { externalTagsToLabels } from "@charphq/ui/utils/deviceHelpers";
import { getFlagWarnings, stripFlagPrefix } from "../../device/device-flag-banner";
import {
  ConfigFingerprintBadge,
  ConfigFingerprintValue,
  type ConfigFingerprint,
} from "../../device/config-fingerprint-badge";
import {
  Clock,
  Tag,
  ChevronRight,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Building2,
  Flag,
} from "lucide-react";

interface AssignmentRef {
  id: string;
  name: string;
}

interface Device {
  id: string;
  name: string;
  devEUI: string;
  externalId?: string | null;
  deviceType?: {
    id: string;
    name: string;
    description?: string;
    category?: string;
  };
  status?: string;
  lastSeenAt?: string;
  externalTags?: Record<string, any> | string[] | null;
  externalConfig?: Record<string, any>;
  serialNumber?: string;
  model?: string;
  manufacturer?: string;
  currentCustomer?: AssignmentRef | null;
  currentProject?: AssignmentRef | null;
  currentDeployment?: AssignmentRef | null;
  warnings?: { code: string; severity: string; message: string }[] | null;
  /**
   * Last reported configuration CRC (OTO-170). Present only for device types with a CRC-based
   * drift method, and present even when the device has no deployment or no organization — the
   * CRC is read from the telemetry snapshot, not from the org-scoped config state.
   */
  configFingerprint?: ConfigFingerprint | null;
}

export type { Device as SearchDevice };

interface DeviceCardProps {
  device: Device;
  onClick?: () => void;
  className?: string;
  autoSync?: boolean;
  onSyncComplete?: (device: Device) => void;
  searchSessionId?: string;
  /** Optional render function for custom action buttons (e.g. "Connect CLI") */
  renderActions?: (device: Device) => React.ReactNode;
  /** Highlight the card as the active keyboard selection. */
  isSelected?: boolean;
}

export function DeviceCard({
  device,
  onClick,
  className,
  autoSync = false,
  onSyncComplete,
  searchSessionId,
  renderActions,
  isSelected,
}: DeviceCardProps) {
  const [isAutoSyncing, setIsAutoSyncing] = useState(false);
  const hasAutoSynced = useRef(false);

  const syncMutation = usePostApiDevicesIdSync({
    mutation: {
      onSuccess: (syncedDevice) => {
        setIsAutoSyncing(false);
        hasAutoSynced.current = true; // Mark as synced
        onSyncComplete?.(syncedDevice.data as unknown as Device);
      },
      onError: () => {
        setIsAutoSyncing(false);
        hasAutoSynced.current = true; // Mark as synced even on error to prevent retry
      },
    },
  });

  // Fetch flight check data for the device
  const { data: flightCheckResponse } = useGetApiDevicesIdFlightCheck(device.id, {
    query: {
      enabled: !!device.id,
      staleTime: 60 * 1000, // Cache for 1 minute
      refetchOnWindowFocus: false,
    },
  });

  const flightCheck = (flightCheckResponse as any)?.data;

  // Reset auto-sync flag when search session changes (new search)
  useEffect(() => {
    hasAutoSynced.current = false;
  }, [searchSessionId]);

  // Auto-sync logic - only sync once per device per search session
  useEffect(() => {
    if (autoSync && !isAutoSyncing && !syncMutation.isPending && !hasAutoSynced.current) {
      setIsAutoSyncing(true);
      syncMutation.mutate({
        id: device.id,
      });
    }
  }, [autoSync, device.id, isAutoSyncing, syncMutation]);

  const getStatusVariant = (status?: string) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "default";
      case "inactive":
        return "secondary";
      case "error":
        return "destructive";
      default:
        return "outline";
    }
  };

  const getFlightCheckBadge = () => {
    if (!flightCheck) return null;

    const status = flightCheck.overallStatus;
    const statusConfig = {
      pass: {
        variant: "default" as const,
        className: "bg-status-ok-soft text-status-ok",
        icon: CheckCircle2,
        label: "Pass",
      },
      fail: {
        variant: "destructive" as const,
        className: "bg-status-alert-soft text-status-alert",
        icon: XCircle,
        label: "Fail",
      },
      warning: {
        variant: "outline" as const,
        className: "bg-status-warning-soft text-status-warning",
        icon: AlertCircle,
        label: "Warning",
      },
    };

    const config = statusConfig[status as keyof typeof statusConfig];
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className={cn("text-xs gap-1", config.className)}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  // Helper function to format last seen time
  const formatLastSeen = (lastSeenAt?: string) => {
    if (!lastSeenAt) return "Never";

    const date = new Date(lastSeenAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  };

  // Flagged entries (OTO-156) reach the card through `device.warnings`, which the device
  // list response already carries and useUniversalSearch spreads through untouched. The
  // badge supplies the "Flagged" wording, so strip the backend's prefix off the messages.
  const flagWarnings = getFlagWarnings(device.warnings);
  const flagCount = flagWarnings.length;
  const flagMessages = flagWarnings.map((w) => stripFlagPrefix(w.message)).join("\n");

  const externalTags = externalTagsToLabels(device.externalTags);

  // Assignment designation (customer › project › deployment) — only the present parts.
  const designation = [
    device.currentCustomer?.name,
    device.currentProject?.name,
    device.currentDeployment?.name,
  ].filter((n): n is string => !!n);

  return (
    <EntityCard
      onClick={onClick}
      className={cn(className, isSelected && "border-primary ring-2 ring-primary/50 shadow-md")}
      isLoading={isAutoSyncing || syncMutation.isPending}
      loadingText={isAutoSyncing ? "Syncing device..." : "Loading..."}
    >
      <div className="flex h-full items-center justify-between">
        {/* Left side - Main content */}
        <div className="flex-1 min-w-0">
          {/* Top row: Device name, model/manufacturer, and status */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 min-w-0">
              <EntityTypeBadge kind="device" />
              <h3 className="font-medium text-base truncate">{device.name}</h3>
              {(device.model || device.manufacturer) && (
                <span className="text-xs text-muted-foreground truncate">
                  {device.manufacturer && device.model
                    ? `${device.manufacturer} • ${device.model}`
                    : device.manufacturer || device.model}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0 ml-2">
              {flagCount > 0 && (
                <Badge
                  variant="alert"
                  className="text-xs gap-1"
                  title={flagMessages}
                  aria-label={`Flagged: ${flagMessages}`}
                >
                  <Flag className="h-3 w-3" />
                  {flagCount > 1 ? `${flagCount} flags` : "Flagged"}
                </Badge>
              )}
              {device.status && (
                <Badge variant={getStatusVariant(device.status)} className="text-xs">
                  {device.status}
                </Badge>
              )}
              {getFlightCheckBadge()}
              {/* Config CRC verdict (OTO-170) — absent for device types with no CRC mechanism. */}
              {device.configFingerprint && (
                <ConfigFingerprintBadge fingerprint={device.configFingerprint} />
              )}
            </div>
          </div>

          {/* Second row: Last seen, DevEUI and the reported config CRC */}
          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2 min-w-0">
            <div className="flex items-center gap-1 shrink-0">
              <Clock className="h-3 w-3" />
              <span>{formatLastSeen(device.lastSeenAt)}</span>
            </div>
            <span className="font-mono text-xs shrink-0">{device.devEUI}</span>
            {device.configFingerprint && (
              <ConfigFingerprintValue
                fingerprint={device.configFingerprint}
                className="min-w-0 truncate"
              />
            )}
          </div>

          {/* Third row: Assignment designation (customer › project › deployment) */}
          {designation.length > 0 && (
            <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2 min-w-0">
              <Building2 className="h-3 w-3 shrink-0" />
              <span className="truncate">{designation.join(" › ")}</span>
            </div>
          )}

          {/* Fourth row: External tags */}
          {externalTags.length > 0 && (
            <div className="flex items-center gap-1">
              <Tag className="h-3 w-3 text-muted-foreground" />
              <div className="flex gap-1 flex-wrap">
                {externalTags.slice(0, 3).map((label, index) => (
                  <span
                    key={`${label}-${index}`}
                    className="bg-muted px-2 py-1 rounded text-xs whitespace-nowrap"
                  >
                    {label}
                  </span>
                ))}
                {externalTags.length > 3 && (
                  <span className="text-xs text-muted-foreground">+{externalTags.length - 3}</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Custom actions (e.g. Connect CLI for Maxi devices) */}
        {renderActions && (
          <div
            className="flex items-center gap-1 shrink-0 ml-2"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {renderActions(device)}
          </div>
        )}

        {/* Right side - Chevron and sync indicator */}
        <div className="flex flex-col items-end gap-2 shrink-0 ml-4">
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
          {isAutoSyncing && <span className="text-xs text-primary font-medium">Syncing...</span>}
        </div>
      </div>
    </EntityCard>
  );
}
